import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, MessageSquare, Send, CheckCircle, XCircle, Clock,
  TrendingUp, Activity, RefreshCw, ArrowUpRight, ArrowDownRight,
  FileText, Building2, Heading, Package, IndianRupee, BarChart3,
  Zap, AlertTriangle, LayoutDashboard
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { formatIndianCurrency, formatIndianRate } from '../utils/numberFormat';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const KPISkeleton = () => (
  <div className="rounded-xl p-4" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
    <div className="flex items-center justify-between mb-2">
      <Skeleton className="w-8 h-8 rounded-lg" />
      <Skeleton className="w-16 h-3" />
    </div>
    <Skeleton className="w-20 h-7 mb-2" />
    <Skeleton className="w-14 h-3" />
  </div>
);

const UserDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const response = await api.get('/user/dashboard/stats');
      setStats(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user dashboard stats:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => fetchStats(true), 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#6366f1' }} />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={() => fetchStats()} className="mt-3 px-4 py-2 text-white rounded-xl hover:opacity-90 text-sm font-medium" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const s = stats || {};
  const bal = s.balance || {};
  const sms = s.sms || {};
  const dlt = s.dlt || {};
  const plan = s.plan || {};
  const smsCapacity = s.sms_capacity || 0;

  const deliveryRate = sms.total > 0 ? ((sms.delivered / sms.total) * 100).toFixed(1) : 0;
  const failureRate = sms.total > 0 ? ((sms.failed / sms.total) * 100).toFixed(1) : 0;
  const todayDeliveryRate = sms.today > 0 ? ((sms.today_delivered / sms.today) * 100).toFixed(1) : 0;

  const kpiCards = [
    {
      title: 'Available Balance',
      value: formatIndianCurrency(bal.available || 0),
      sub: smsCapacity > 0 ? `≈ ${smsCapacity.toLocaleString()} SMS remaining` : `Rate: ${formatIndianRate(plan.rate_per_sms || 0)}/SMS`,
      icon: Wallet,
      gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      bgColor: 'rgba(16, 185, 129, 0.06)',
      borderColor: 'rgba(16, 185, 129, 0.12)',
    },
    {
      title: "Today's SMS",
      value: (sms.today || 0).toLocaleString(),
      sub: `${sms.today_delivered || 0} delivered`,
      icon: Send,
      gradient: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
      bgColor: 'rgba(99, 102, 241, 0.06)',
      borderColor: 'rgba(99, 102, 241, 0.12)',
      label: 'Today',
    },
    {
      title: 'Total SMS',
      value: (sms.total || 0).toLocaleString(),
      sub: `${deliveryRate}% delivery rate`,
      icon: MessageSquare,
      gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      bgColor: 'rgba(6, 182, 212, 0.06)',
      borderColor: 'rgba(6, 182, 212, 0.12)',
      label: 'This Month',
    },
    {
      title: 'Delivered',
      value: (sms.delivered || 0).toLocaleString(),
      sub: `${deliveryRate}% success`,
      icon: CheckCircle,
      gradient: 'linear-gradient(135deg, #059669 0%, #34d399 100%)',
      bgColor: 'rgba(16, 185, 129, 0.06)',
      borderColor: 'rgba(16, 185, 129, 0.12)',
    },
    {
      title: 'Failed',
      value: (sms.failed || 0).toLocaleString(),
      sub: `${failureRate}% failure`,
      icon: XCircle,
      gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      bgColor: 'rgba(239, 68, 68, 0.06)',
      borderColor: 'rgba(239, 68, 68, 0.12)',
    },
    {
      title: 'Pending',
      value: (sms.pending || 0).toLocaleString(),
      sub: 'In queue',
      icon: Clock,
      gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
      bgColor: 'rgba(245, 158, 11, 0.06)',
      borderColor: 'rgba(245, 158, 11, 0.12)',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            Welcome back, {user?.full_name || user?.username}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Your SMS platform overview
            {lastUpdated && <span className="ml-2">· Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <button
          onClick={() => fetchStats(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm transition-all hover:shadow-md disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <KPISkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpiCards.map((card, i) => (
            <div key={i} className="rounded-xl p-4 transition-all duration-200 hover:shadow-md" style={{
              background: card.bgColor,
              border: `1px solid ${card.borderColor}`,
            }}>
              <div className="flex items-center justify-between mb-2 gap-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: card.gradient }}>
                  <card.icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[9px] lg:text-[10px] font-semibold text-gray-400 uppercase tracking-wide text-right leading-tight truncate">{card.title}</span>
              </div>
              <div className="text-xl font-bold text-gray-900 truncate">{card.value}</div>
              <div className="text-[11px] text-gray-500 mt-1 truncate">
                {card.label && <span className="text-[9px] font-bold text-indigo-400 uppercase mr-1">{card.label}</span>}
                {card.sub}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Row 2: Billing + DLT Summary + Today's Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Billing & Rate */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <IndianRupee className="w-4 h-4" style={{ color: '#6366f1' }} /> Billing & Rate
            <span className="ml-auto text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">Account</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Rate per SMS</span>
              <span className="text-sm font-bold" style={{ color: '#4f46e5' }}>{formatIndianRate(plan.rate_per_sms || 0)}</span>
            </div>
            <div className="pt-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total Recharged</span>
                <span className="text-sm font-bold text-green-600">{formatIndianCurrency(bal.total_recharged || 0)}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-gray-500">Total Spent</span>
                <span className="text-sm font-bold text-red-500">{formatIndianCurrency(bal.total_spent || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* DLT Summary */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" style={{ color: '#6366f1' }} /> DLT Summary
            <span className="ml-auto text-[9px] font-bold text-emerald-500 uppercase px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-100">Active</span>
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(139, 92, 246, 0.06)' }}>
              <Building2 className="w-5 h-5 mx-auto mb-1" style={{ color: '#8b5cf6' }} />
              <div className="text-xl font-bold text-gray-900">{dlt.entities || 0}</div>
              <div className="text-xs text-gray-500">Entities</div>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(99, 102, 241, 0.06)' }}>
              <Heading className="w-5 h-5 mx-auto mb-1" style={{ color: '#6366f1' }} />
              <div className="text-xl font-bold text-gray-900">{dlt.headers || 0}</div>
              <div className="text-xs text-gray-500">Headers</div>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(6, 182, 212, 0.06)' }}>
              <FileText className="w-5 h-5 mx-auto mb-1" style={{ color: '#06b6d4' }} />
              <div className="text-xl font-bold text-gray-900">{dlt.templates || 0}</div>
              <div className="text-xs text-gray-500">Templates</div>
            </div>
          </div>
        </div>

        {/* Today's Performance */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4" style={{ color: '#f59e0b' }} /> Today's Performance
            <span className="ml-auto text-[9px] font-bold text-amber-500 uppercase px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100">Today</span>
          </h3>
          {/* Live delivery rate bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-500">Delivery Rate</span>
              <span className="text-sm font-bold" style={{ color: Number(todayDeliveryRate) >= 80 ? '#059669' : Number(todayDeliveryRate) >= 50 ? '#d97706' : '#dc2626' }}>{todayDeliveryRate}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${Math.min(todayDeliveryRate, 100)}%`, background: Number(todayDeliveryRate) >= 80 ? 'linear-gradient(90deg, #059669, #34d399)' : Number(todayDeliveryRate) >= 50 ? 'linear-gradient(90deg, #d97706, #fbbf24)' : 'linear-gradient(90deg, #dc2626, #f87171)' }} />
            </div>
          </div>
          {/* 4-cell grid */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: Send, label: 'Total Sent', value: sms.today || 0, bg: 'rgba(99, 102, 241, 0.06)', iconColor: '#6366f1', valueColor: '#4f46e5' },
              { icon: CheckCircle, label: 'Delivered', value: sms.today_delivered || 0, bg: 'rgba(16, 185, 129, 0.06)', iconColor: '#059669', valueColor: '#059669' },
              { icon: XCircle, label: 'Failed', value: sms.today_failed || 0, bg: 'rgba(239, 68, 68, 0.06)', iconColor: '#dc2626', valueColor: '#dc2626' },
              { icon: Clock, label: 'Pending', value: Math.max((sms.today || 0) - (sms.today_delivered || 0) - (sms.today_failed || 0), 0), bg: 'rgba(245, 158, 11, 0.06)', iconColor: '#d97706', valueColor: '#d97706' },
            ].map((item, idx) => (
              <div key={idx} className="rounded-xl p-2.5 flex items-center gap-2.5" style={{ background: item.bg }}>
                <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: item.iconColor }} />
                <div>
                  <div className="text-base font-bold" style={{ color: item.valueColor }}>{item.value.toLocaleString()}</div>
                  <div className="text-[10px] text-gray-500 font-medium">{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 7-Day SMS Trend */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: '#6366f1' }} /> SMS Trend (Last 7 Days)
            <span className="ml-auto text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">Summary</span>
          </h3>
          {loading ? (
            <Skeleton className="w-full h-48" />
          ) : (
            <SMSTrendChart data={sms.trend_7d || []} />
          )}
        </div>

        {/* Delivery Status Donut */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4" style={{ color: '#6366f1' }} /> Delivery Status Distribution
            <span className="ml-auto text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">This Month</span>
          </h3>
          {loading ? (
            <Skeleton className="w-full h-48" />
          ) : (
            <DeliveryDonutChart
              delivered={sms.delivered || 0}
              failed={sms.failed || 0}
              pending={sms.pending || 0}
              total={sms.total || 0}
            />
          )}
        </div>
      </div>

      {/* Row 4: Hourly Distribution */}
      <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" style={{ color: '#6366f1' }} /> Today's Hourly Distribution
          <span className="ml-auto text-[9px] font-bold text-amber-500 uppercase px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100">Live</span>
        </h3>
        {(sms.hourly_today && sms.hourly_today.length > 0) ? (
          <HourlyChart data={sms.hourly_today} />
        ) : (
          <div className="flex flex-col items-center justify-center h-40 rounded-xl" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
            <BarChart3 className="w-8 h-8 mb-2" style={{ color: '#c7d2fe' }} />
            <p className="text-sm text-gray-400">No hourly data yet</p>
            <p className="text-xs text-gray-300 mt-1">Hourly breakdown will appear as SMS are sent today</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Chart Components ───────────────────────────────────────────────────────

const CHART_COLORS = { delivered: '#059669', failed: '#dc2626', pending: '#d97706', total: '#4f46e5' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl p-3 shadow-lg text-xs" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
      <p className="font-bold text-gray-800 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-500 capitalize">{p.dataKey}:</span>
          <span className="font-bold text-gray-800">{(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

// 7-Day SMS Trend — recharts BarChart
const SMSTrendChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 rounded-xl" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
        <TrendingUp className="w-8 h-8 mb-2" style={{ color: '#c7d2fe' }} />
        <p className="text-sm text-gray-400">No trend data available</p>
        <p className="text-xs text-gray-300 mt-1">SMS data will appear here once messages are sent</p>
      </div>
    );
  }

  const chartData = data.map(d => {
    const dt = new Date(d.date);
    return {
      name: dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit' }),
      delivered: d.delivered || 0,
      failed: d.failed || 0,
      pending: Math.max((d.total || 0) - (d.delivered || 0) - (d.failed || 0), 0),
      total: d.total || 0,
    };
  });

  return (
    <div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.08)" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => v > 999 ? `${(v/1000).toFixed(1)}k` : v} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99,102,241,0.04)' }} />
          <Bar dataKey="delivered" stackId="a" fill={CHART_COLORS.delivered} radius={[0, 0, 0, 0]} name="Delivered" />
          <Bar dataKey="failed" stackId="a" fill={CHART_COLORS.failed} radius={[0, 0, 0, 0]} name="Failed" />
          <Bar dataKey="pending" stackId="a" fill={CHART_COLORS.pending} radius={[4, 4, 0, 0]} name="Pending" />
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-5 mt-2 pt-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{background: CHART_COLORS.delivered}} /><span className="text-[11px] text-gray-500">Delivered</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{background: CHART_COLORS.failed}} /><span className="text-[11px] text-gray-500">Failed</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{background: CHART_COLORS.pending}} /><span className="text-[11px] text-gray-500">Pending</span></div>
      </div>
    </div>
  );
};

// Delivery Status Distribution — recharts PieChart with sidebar stats
const DeliveryDonutChart = ({ delivered, failed, pending, total }) => {
  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 rounded-xl" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
        <Activity className="w-8 h-8 mb-2" style={{ color: '#c7d2fe' }} />
        <p className="text-sm text-gray-400">No SMS data yet</p>
        <p className="text-xs text-gray-300 mt-1">Status distribution will appear here</p>
      </div>
    );
  }

  const segments = [
    { name: 'Delivered', value: delivered, color: CHART_COLORS.delivered },
    { name: 'Failed', value: failed, color: CHART_COLORS.failed },
    { name: 'Pending', value: pending, color: CHART_COLORS.pending },
  ].filter(s => s.value > 0);

  const PieLabel = ({ cx, cy }) => (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
      <tspan x={cx} dy="-6" className="text-lg font-bold" fill="#1f2937">{total.toLocaleString()}</tspan>
      <tspan x={cx} dy="18" className="text-[10px]" fill="#9ca3af">Total SMS</tspan>
    </text>
  );

  return (
    <div className="flex items-center gap-4">
      <div className="flex-shrink-0" style={{ width: 160, height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={segments} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={2} dataKey="value" stroke="none" content={<PieLabel />}>
              {segments.map((s, i) => <Cell key={i} fill={s.color} />)}
              <PieLabel />
            </Pie>
            <Tooltip formatter={(val) => val.toLocaleString()} contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid rgba(99,102,241,0.12)' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-3 flex-1 min-w-0">
        {[
          { label: 'Delivered', value: delivered, pct: total > 0 ? ((delivered / total) * 100).toFixed(1) : 0, color: CHART_COLORS.delivered },
          { label: 'Failed', value: failed, pct: total > 0 ? ((failed / total) * 100).toFixed(1) : 0, color: CHART_COLORS.failed },
          { label: 'Pending', value: pending, pct: total > 0 ? ((pending / total) * 100).toFixed(1) : 0, color: CHART_COLORS.pending },
        ].map((seg, i) => (
          <div key={i}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: seg.color }} />
                <span className="text-xs font-medium text-gray-600">{seg.label}</span>
              </div>
              <span className="text-xs font-bold text-gray-900">{seg.value.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${seg.pct}%`, background: seg.color }} />
              </div>
              <span className="text-[10px] text-gray-400 w-10 text-right font-medium">{seg.pct}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Hourly Distribution — recharts AreaChart with gradient fill
const HourlyChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const hourMap = {};
  data.forEach(d => { hourMap[d.hour] = d.count; });
  const chartData = Array.from({ length: 24 }, (_, i) => ({
    hour: `${String(i).padStart(2, '0')}:00`,
    sms: hourMap[i] || 0,
  }));

  const totalToday = chartData.reduce((sum, h) => sum + h.sms, 0);
  const peakHour = chartData.reduce((max, h) => h.sms > max.sms ? h : max, chartData[0]);

  const HourlyTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white rounded-xl p-2.5 shadow-lg text-xs" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
        <p className="font-bold text-gray-800">{label}</p>
        <p className="text-indigo-600 font-semibold">{(payload[0].value || 0).toLocaleString()} SMS</p>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 px-1">
        <span className="text-xs text-gray-400">24-hour SMS volume</span>
        <div className="flex items-center gap-4">
          <span className="text-[11px] text-gray-500">Peak: <strong className="text-indigo-600">{peakHour.hour}</strong> ({peakHour.sms.toLocaleString()})</span>
          <span className="text-[11px] font-semibold" style={{ color: '#4f46e5' }}>Total: {totalToday.toLocaleString()}</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
          <defs>
            <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.06)" vertical={false} />
          <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={2} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => v > 999 ? `${(v/1000).toFixed(1)}k` : v} />
          <Tooltip content={<HourlyTooltip />} />
          <Area type="monotone" dataKey="sms" stroke="#4f46e5" strokeWidth={2} fill="url(#hourlyGradient)" dot={false} activeDot={{ r: 4, fill: '#4f46e5', stroke: '#fff', strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default UserDashboard;
